import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
    EditableContainer,
    WordCountDisplay,
    ResetIconButton,
    ActionButtonsBox,
    ActionButton,
    editableStyles,
    getHtmlContent,
    countWords,
    calculateWordDiff,
    handlePasteWithFormatting,
} from '../../../assets/styles/SlideEditStyles';

const CombinedEditableSection = ({
    slide,
    englishContent = '',
    isEditMode,
    slideEdits = {},
    showEnglishContent = false,
    onSave,
    onReset,
    onCancel,
    children,
    TitleComponent,
    ContentComponent,
    titleExtraProps = {},
    contentExtraProps = {},
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [wordCount, setWordCount] = useState(0);
    const titleRef = useRef(null);
    const contentRef = useRef(null);
    const prevShowEnglishRef = useRef(null);
    const nonEnglishDraftTitleRef = useRef(null); // null = not set, string = draft content
    const nonEnglishDraftContentRef = useRef(null);
    const isInitializedRef = useRef(false); // Track if we've initialized content

    // Treat English view as fully non-editable (including hover affordances).
    const isEditModeActive = Boolean(isEditMode && !showEnglishContent);

    // When English content is being shown, keep this section fully read-only:
    // - don't allow entering edit mode
    // - if the user toggles to English while editing, exit editing mode
    useEffect(() => {
        if (showEnglishContent && isEditing) {
            setIsEditing(false);
        }
    }, [showEnglishContent, isEditing]);

    // Get content as string from various formats
    const getContentAsString = useCallback(() => {
        if (typeof slide.SlideContent === 'string') {
            return slide.SlideContent;
        } else if (Array.isArray(slide.SlideContent)) {
            return slide.SlideContent.map(item => {
                if (typeof item === 'string') return item;
                if (item && typeof item === 'object') {
                    const parts = [];
                    if (item.Title) parts.push(item.Title);
                    if (item.Text) parts.push(item.Text);
                    if (item.SubTitle) parts.push(item.SubTitle);
                    return parts.join(' ');
                }
                return '';
            })
            .filter(Boolean)
            .join(' ');
        }
        return '';
    }, [slide.SlideContent]);

    const origin = slide?._pbdq_origin || slide || {};
    const rawSlideTitle = slide?.SlideTitle || origin?.headline || origin?.title || '';

    // Calculate word count difference
    const calculateWordCount = useCallback(() => {
        if (!titleRef.current || !contentRef.current) return 0;
        const combineContent = `${rawSlideTitle} ${getContentAsString()}`;
        const originalWordCount = countWords(combineContent);
        const currentCombined = `${titleRef.current.innerHTML || ''} ${contentRef.current.innerHTML || ''}`;
        const currentWordCount = countWords(currentCombined);
        
        return currentWordCount - originalWordCount;
    }, [rawSlideTitle, getContentAsString]);

    // Real-time word count update handler - always update non-English draft
    const handleInputChange = useCallback(() => {
        const newCount = calculateWordCount();
        setWordCount(newCount);
        // Only update draft if we're editing non-English content
        if (!showEnglishContent && titleRef.current && contentRef.current) {
            nonEnglishDraftTitleRef.current = titleRef.current.innerHTML || '';
            nonEnglishDraftContentRef.current = contentRef.current.innerHTML || '';
        }
    }, [calculateWordCount, showEnglishContent]);

    // Initialize when entering edit mode
    useEffect(() => {
        if (isEditing) {
            setTimeout(() => {
                if (titleRef.current && contentRef.current) {
                    const titleEdit = slideEdits['title'];
                    const contentEdit = slideEdits['content_0'];
                    
                    // Determine initial content based on language and draft state
                    let initialTitleHtml, initialContentHtml;
                    
                    if (showEnglishContent) {
                        // English: always show original
                        // Priority: original from saved edit > englishContent prop > original slide content
                        initialTitleHtml = getHtmlContent(titleEdit?.original || rawSlideTitle || '');
                        initialContentHtml = getHtmlContent(contentEdit?.original || englishContent || getContentAsString() || '');
                    } else {
                        // Non-English: prioritize draft > saved edit > original
                        initialTitleHtml = nonEnglishDraftTitleRef.current !== null
                            ? nonEnglishDraftTitleRef.current
                            : getHtmlContent(titleEdit?.edited || rawSlideTitle || '');
                        initialContentHtml = nonEnglishDraftContentRef.current !== null
                            ? nonEnglishDraftContentRef.current
                            : getHtmlContent(contentEdit?.edited || getContentAsString() || '');
                    }
                    
                    titleRef.current.innerHTML = initialTitleHtml;
                    contentRef.current.innerHTML = initialContentHtml;
                    
                    // Set contentEditable based on language
                    titleRef.current.contentEditable = showEnglishContent ? 'false' : 'true';
                    contentRef.current.contentEditable = showEnglishContent ? 'false' : 'true';
                    titleRef.current.style.cursor = showEnglishContent ? 'not-allowed' : 'text';
                    contentRef.current.style.cursor = showEnglishContent ? 'not-allowed' : 'text';
                    
                    // If we're initializing non-English and no draft exists, set draft to initial content
                    if (!showEnglishContent && nonEnglishDraftTitleRef.current === null) {
                        nonEnglishDraftTitleRef.current = initialTitleHtml;
                        nonEnglishDraftContentRef.current = initialContentHtml;
                    }
                    
                    // Update word count after setting initial content
                    setTimeout(() => {
                        setWordCount(calculateWordCount());
                    }, 0);
                    
                    if (!showEnglishContent) {
                        titleRef.current.focus();
                    }
                    
                    isInitializedRef.current = true;
                }
            }, 0);
        } else {
            setWordCount(0);
            isInitializedRef.current = false;
        }
    }, [isEditing, slide, slideEdits, calculateWordCount, getContentAsString, showEnglishContent, englishContent]);

    // Handle language toggle while editing
    useEffect(() => {
        if (!isEditing || !titleRef.current || !contentRef.current) return;

        // Only proceed if language actually changed
        if (prevShowEnglishRef.current === showEnglishContent) return;

        const titleEdit = slideEdits['title'];
        const contentEdit = slideEdits['content_0'];

        if (showEnglishContent) {
            // Switching to English: store current non-English draft
            nonEnglishDraftTitleRef.current = titleRef.current.innerHTML || '';
            nonEnglishDraftContentRef.current = contentRef.current.innerHTML || '';

            // Show English content (read-only)
            const englishTitle = rawSlideTitle || '';
            const englishContentToShow = getContentAsString() || '';

            titleRef.current.innerHTML = getHtmlContent(englishTitle);
            contentRef.current.innerHTML = getHtmlContent(englishContentToShow);
            titleRef.current.contentEditable = 'false';
            contentRef.current.contentEditable = 'false';
            titleRef.current.style.cursor = 'not-allowed';
            contentRef.current.style.cursor = 'not-allowed';
        } else {
            // Switching back to non-English: restore draft
            const restoredTitle = nonEnglishDraftTitleRef.current !== null
                ? nonEnglishDraftTitleRef.current
                : getHtmlContent(titleEdit?.edited || rawSlideTitle || '');
            const restoredContent = nonEnglishDraftContentRef.current !== null
                ? nonEnglishDraftContentRef.current
                : getHtmlContent(contentEdit?.edited || getContentAsString() || '');

            titleRef.current.innerHTML = restoredTitle;
            contentRef.current.innerHTML = restoredContent;
            titleRef.current.contentEditable = 'true';
            contentRef.current.contentEditable = 'true';
            titleRef.current.style.cursor = 'text';
            contentRef.current.style.cursor = 'text';
        }

        // Update word count for non-English content
        setTimeout(() => setWordCount(calculateWordCount()), 0);

        prevShowEnglishRef.current = showEnglishContent;
    }, [showEnglishContent, isEditing, slideEdits, slide, rawSlideTitle, getContentAsString]);

    const handleClick = () => {
        // If English content is displayed, this section must not be selectable/focusable for editing.
        if (showEnglishContent) return;
        if (isEditModeActive && !isEditing) {
            setIsEditing(true);
        }
    };

    const handleSave = () => {
        if (onSave && titleRef.current && contentRef.current) {
            const currentTitleHtml = titleRef.current.innerHTML || '';
            const currentContentHtml = contentRef.current.innerHTML || '';

            // Update local draft refs immediately so toggling works
            nonEnglishDraftTitleRef.current = currentTitleHtml;
            nonEnglishDraftContentRef.current = currentContentHtml;

            // Notify parent about title/content edit
            onSave('title', {
                edited: currentTitleHtml,
                language: showEnglishContent ? 'en' : 'other', // let parent know language
            });
            onSave('content_0', {
                edited: currentContentHtml,
                language: showEnglishContent ? 'en' : 'other',
            });
        }

        setIsEditing(false);
    };


    const handleReset = () => {
        if (onReset) {
            onReset('title');
            onReset('content_0');
        }
        if (titleRef.current && contentRef.current) {
            const originalTitleHtml = getHtmlContent(rawSlideTitle || '');
            const originalContentHtml = getHtmlContent(getContentAsString());
            
            titleRef.current.innerHTML = originalTitleHtml;
            contentRef.current.innerHTML = originalContentHtml;
            
            // Clear drafts and reset to original
            nonEnglishDraftTitleRef.current = originalTitleHtml;
            nonEnglishDraftContentRef.current = originalContentHtml;
            
            // Update word count after resetting content
            setTimeout(() => {
                setWordCount(calculateWordCount());
            }, 0);
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel('title');
            onCancel('content_0');
        }
        setIsEditing(false);
    };

    const displayTitle = showEnglishContent ? (slideEdits['title']?.original || rawSlideTitle) : (slideEdits['title']?.edited || rawSlideTitle);
    const displayContent = showEnglishContent ? (slideEdits['content_0']?.original || getContentAsString()) : (slideEdits['content_0']?.edited || getContentAsString());

    return (
        <EditableContainer
            isEditMode={isEditModeActive}
            isHovered={isHovered}
            isEditing={isEditing}
            onMouseEnter={() => {
                if (showEnglishContent) return;
                if (isEditModeActive && !isEditing) setIsHovered(true);
            }}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick}
        >
            {isEditing && (
                <>
                    {/* Word Count - Shows changed words count (only for non-English content) */}
                    {!showEnglishContent && (
                        <WordCountDisplay>
                            {Math.abs(wordCount)} {Math.abs(wordCount) === 1 ? 'word' : 'words'} changed
                        </WordCountDisplay>
                    )}
                    
                    {/* Reset Button - Icon only, top-right (only for non-English content) */}
                    {!showEnglishContent && (
                        <ResetIconButton
                            size="small"
                            onClick={(e) => { 
                                e.stopPropagation(); 
                                handleReset(); 
                            }}
                            title="Reset to original"
                        >
                            <RefreshIcon fontSize="small" />
                        </ResetIconButton>
                    )}
                </>
            )}

            {isEditing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <TitleComponent
                        ref={titleRef}
                        contentEditable
                        suppressContentEditableWarning
                        {...titleExtraProps}
                        sx={{
                            ...editableStyles,
                            ...(titleExtraProps.sx || {}),
                        }}
                        style={{
                            outline: 'none',
                            minHeight: '40px',
                            padding: '4px',
                            paddingTop: '24px',
                            wordBreak: 'break-word',
                            ...(titleExtraProps.style || {}),
                        }}
                        onInput={handleInputChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                handleCancel();
                            }
                        }}
                        onPaste={handlePasteWithFormatting}
                    />
                    <ContentComponent
                        ref={contentRef}
                        contentEditable
                        suppressContentEditableWarning
                        {...contentExtraProps}
                        sx={{
                            ...editableStyles,
                            ...(contentExtraProps.sx || {}),
                        }}
                        style={{
                            outline: 'none',
                            minHeight: '40px',
                            padding: '4px',
                            wordBreak: 'break-word',
                            ...(contentExtraProps.style || {}),
                        }}
                        onInput={handleInputChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.preventDefault();
                                handleCancel();
                            }
                        }}
                        onPaste={handlePasteWithFormatting}
                    />
                    {/* Action buttons (only for non-English content) */}
                    {!showEnglishContent && (
                        <ActionButtonsBox>
                            <ActionButton
                                actionType="save"
                                onClick={(e) => { e.stopPropagation(); handleSave(); }}
                            >
                                Save
                            </ActionButton>
                            <ActionButton
                                actionType="cancel"
                                onClick={(e) => { e.stopPropagation(); handleCancel(); }}
                            >
                                Cancel
                            </ActionButton>
                        </ActionButtonsBox>
                    )}
                </Box>
            ) : (
                children || (
                    <>
                        <TitleComponent
                            {...titleExtraProps}
                            sx={{
                                ...(titleExtraProps.sx || {}),
                                ...(showEnglishContent ? {
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    MozUserSelect: 'none',
                                    msUserSelect: 'none',
                                } : {}),
                            }}
                        >
                            <span dangerouslySetInnerHTML={{ __html: displayTitle }} />
                        </TitleComponent>
                        <ContentComponent
                            {...contentExtraProps}
                            sx={{
                                ...(contentExtraProps.sx || {}),
                                ...(showEnglishContent ? {
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    MozUserSelect: 'none',
                                    msUserSelect: 'none',
                                } : {}),
                            }}
                        >
                            <span dangerouslySetInnerHTML={{ __html: displayContent }} />
                        </ContentComponent>
                    </>
                )
            )}
        </EditableContainer>
    );
};

export default CombinedEditableSection;
